/**
 * DashboardCanvasEditor Component (Dashboards feature)
 *
 * A WEB-STYLE clone of the report `CanvasEditor` (apps/web/src/components/reports/
 * CanvasEditor.tsx). It keeps the EXACT SAME public props signature
 * (`CanvasEditorProps`) — so the dashboard host swaps it in with a one-line import
 * change — and reuses ALL editor tooling BY IMPORT from components/reports/
 * (palette, WidgetConfigPanel, ReportDndProvider, reportDnd, gridUtils,
 * WidgetRenderer, the live↔mock data merge, keyboard shortcuts).
 *
 * The ONE difference: the canvas is a CONTINUOUS, FULL-WIDTH web surface, not an A4
 * sheet. The ZoomWrapper drops the fixed A4 `contentWidthPx` and lets the surface
 * fill 100% of the canvas area; the measured area width is threaded into
 * DashboardCanvas so the 12-col grid + dnd math use the REAL width. Zoom stays
 * functional (the controls drive the same translate/scale transform), but the
 * surface is width-flexible rather than locked to paper.
 */

import { useState, useCallback, useMemo, useEffect, useRef, type CSSProperties } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "@phosphor-icons/react";
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@provenleap/ui";
import { DashboardCanvas } from "./DashboardCanvas";
import { isPresetType } from "../reports/WidgetPalette";
import type { WidgetType, PresetType } from "../reports/WidgetPalette";
import {
  getNextAvailablePosition,
  calculateGridPlacement,
  getSmartDefaults,
  MAX_WIDGETS,
} from "../../lib/gridUtils";
import type { ValidColSpan, SmartDefaultWidgetType } from "../../lib/gridUtils";
import type { WidgetConfig } from "../../lib/templateUtils";
import { generateMockData } from "../../lib/mockWidgetData";
import type { WidgetData } from "../../lib/widgetData";
import { widgetHasDataBinding } from "../../lib/widgetCompatibility";
import { useAvailableMetrics } from "../../hooks/useAvailableMetrics";
import { useReportData } from "../../hooks/useReportData";
import { useRollupData } from "../../hooks/useRollupData";
import { useDebounce } from "../../hooks/useDebounce";
import { isClientConnected } from "../../lib/reportClientSource";
import { getRegistryForPlatform, ADAPTER_META } from "@provenleap/integrations";
import type { MetricDefinition } from "@provenleap/integrations";
import { resolveDefaultMetric, applyDefaultMetricToConfig } from "../../lib/metricCompatibility";
import { parseDragPayload } from "../../lib/dragPayload";
import { useToast } from "../../hooks/useToast";
import { useReportDnd } from "../reports/ReportDndProvider";
import type { PaletteDragData, GridCell } from "../../lib/reportDnd";

type RegistryPlatform = Parameters<typeof getRegistryForPlatform>[0];
import { Toast } from "../clients/Toast";
import { WidgetCommandPalette } from "../reports/WidgetCommandPalette";
import { expandPreset, PRESET_LABELS } from "../../lib/widgetPresets";
// Reuse the report editor's defaults (single source of truth, byte-for-byte).
import { DEFAULT_WIDGET_TITLES, DEFAULT_WIDGET_CONFIGS, nextWidgetTitle } from "../reports/canvasWidgetDefaults";
// Re-export so a host migrating from CanvasEditor keeps the same import surface.
export { DEFAULT_WIDGET_TITLES, DEFAULT_WIDGET_CONFIGS };
// The palette / floating panels are paper-agnostic — re-export from the report
// editor so the host can keep importing them from one place if it wishes.
export { EditorRightSidebar, FloatingCategoryPicker, FloatingWidgetSettings } from "../reports/CanvasEditor";
export type { CanvasEditorProps as DashboardCanvasEditorProps } from "../reports/CanvasEditor";
import type { CanvasEditorProps } from "../reports/CanvasEditor";

/** Valid droppable widget types = the keys of `DEFAULT_WIDGET_CONFIGS`. */
const DROPPABLE_WIDGET_TYPES = new Set<string>(Object.keys(DEFAULT_WIDGET_CONFIGS));

function isKnownDropType(type: string): boolean {
  return DROPPABLE_WIDGET_TYPES.has(type) || isPresetType(type);
}

/** Shifts all widgets down by the given number of rows (cover-page insert). */
function shiftWidgetsDown(widgets: WidgetConfig[], rows: number): WidgetConfig[] {
  return widgets.map((w) => ({
    ...w,
    position: { ...w.position, row: w.position.row + rows },
  }));
}

// ── Zoom Wrapper (full-width web variant) ────────────────────────────
//
// Same unified translate(panX,panY) scale(scale) model as the report editor's
// ZoomWrapper, BUT the inner surface is FULL-WIDTH (100% of the measured area)
// instead of the fixed A4 `contentWidthPx`. So the canvas reads as a continuous
// web dashboard that fills the available width, and zoom scales that surface.

function ZoomWrapper({
  zoomLevel,
  leftShift = 0,
  panX = 0,
  panY = 0,
  onMeasure,
  children,
}: {
  zoomLevel: number;
  leftShift?: number;
  panX?: number;
  panY?: number;
  /** Reports the measured (unscaled) inner surface width so the grid pitch matches. */
  onMeasure?: (widthPx: number) => void;
  children: React.ReactNode;
}) {
  const scale = zoomLevel / 100;

  const outerStyle: CSSProperties = {
    position: "relative",
    width: "100%",
    height: "100%",
    transform: `translateX(${-leftShift}px)`,
    transition: "transform 0.25s ease",
  };

  // FULL-WIDTH surface: width 100% (not the A4 contentWidthPx). The zoom scales it
  // from the top-left, same as the report editor, so pan/zoom-to-cursor stays sane.
  const zoomStyle: CSSProperties = {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    transform: `translate(${panX}px, ${panY}px) scale(${scale})`,
    transformOrigin: "0 0",
  };

  // Measure the inner surface's unscaled width so DashboardCanvas's grid pitch maps
  // to the real surface (the grid positions widgets in unscaled px; the parent
  // transform scales them visually).
  const innerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = innerRef.current;
    if (!el || !onMeasure || typeof ResizeObserver === "undefined") return;
    const report = () => onMeasure(el.clientWidth);
    report();
    const ro = new ResizeObserver(report);
    ro.observe(el);
    return () => ro.disconnect();
  }, [onMeasure]);

  return (
    <div data-testid="canvas-editor" style={outerStyle}>
      <div ref={innerRef} data-testid="zoom-wrapper" style={zoomStyle}>
        {children}
      </div>
    </div>
  );
}

export function DashboardCanvasEditor({
  widgets,
  onWidgetsChange,
  onWidgetsSilentChange,
  onUndo,
  onRedo,
  initialWidgets,
  comparisonEnabled,
  reportDateRangeStart,
  reportDateRangeEnd,
  comparisonStart,
  comparisonEnd,
  selectedClientName,
  selectedClientLogoUrl,
  clientId,
  connectedPlatforms,
  reportKind = "client",
  memberClientIds,
  format = "document",
  onSelectedWidgetChange,
  registerDeselect,
  registerSelect,
  registerWidgetActions,
  zoomLevel = 100,
  paperLeftShift = 0,
  panX = 0,
  panY = 0,
}: CanvasEditorProps) {
  const [selectedWidgetId, setSelectedWidgetId] = useState<string | null>(null);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [dismissedErrorMessage, setDismissedErrorMessage] = useState<string | null>(null);

  // Measured full-width surface width — threaded into DashboardCanvas so the grid
  // pitch maps to the real surface (not the A4 width).
  const [measuredWidthPx, setMeasuredWidthPx] = useState(0);
  const handleMeasure = useCallback((w: number) => {
    setMeasuredWidthPx((prev) => (Math.abs(prev - w) > 0.5 ? w : prev));
  }, []);

  useEffect(() => {
    registerDeselect?.(() => setSelectedWidgetId(null));
  }, [registerDeselect]);

  useEffect(() => {
    registerSelect?.((widgetId: string) => setSelectedWidgetId(widgetId));
  }, [registerSelect]);

  const { byIntegration: connectedMetricsByPlatform } = useAvailableMetrics(clientId);
  const { toast: canvasToast, showToast: showCanvasToast } = useToast();

  // ── Live data wiring (verbatim from the report editor) ───────────────
  //
  // Story 40-7 (AC6): mirrors the 40-6 CanvasEditor roll-up branch. A ROLL-UP
  // dashboard has no single clientId — it's "connected" when it has ≥1 resolved
  // member AND ≥1 platform in the member-union `connectedPlatforms` the host
  // passes. Both kinds funnel through the SAME `isConnectedClient` boolean so the
  // live↔mock merge below stays a single decision point (and an unconnected
  // roll-up shows mock exactly like a disconnected single client).
  const isRollup = reportKind === "rollup";
  const rollupMemberIds = useMemo(
    () => (isRollup ? memberClientIds ?? [] : []),
    [isRollup, memberClientIds],
  );
  const isConnectedClient = isRollup
    ? rollupMemberIds.length > 0 && (connectedPlatforms?.length ?? 0) > 0
    : isClientConnected(clientId, connectedPlatforms);

  const liveStart = reportDateRangeStart ?? "";
  const liveEnd = reportDateRangeEnd ?? "";
  const liveComparisonStart = comparisonEnabled ? comparisonStart ?? "" : "";
  const liveComparisonEnd = comparisonEnabled ? comparisonEnd ?? "" : "";
  const rangeKey = JSON.stringify([liveStart, liveEnd, liveComparisonStart, liveComparisonEnd]);
  const debouncedRangeKey = useDebounce(rangeKey, 400);
  const [dStart, dEnd, dComparisonStart, dComparisonEnd] = useMemo(() => {
    try {
      const parsed = JSON.parse(debouncedRangeKey) as unknown;
      if (Array.isArray(parsed) && parsed.length === 4 && parsed.every((p) => typeof p === "string")) {
        return parsed as [string, string, string, string];
      }
    } catch {
      // fall through to live values
    }
    return [liveStart, liveEnd, liveComparisonStart, liveComparisonEnd] as [string, string, string, string];
  }, [debouncedRangeKey, liveStart, liveEnd, liveComparisonStart, liveComparisonEnd]);
  const debouncedComparison = useMemo(
    () =>
      dComparisonStart && dComparisonEnd
        ? { start: dComparisonStart, end: dComparisonEnd }
        : null,
    [dComparisonStart, dComparisonEnd],
  );

  // Story 40-7 (AC6): BOTH hooks are called UNCONDITIONALLY (Rules of Hooks) but
  // only ONE is ever enabled — the single-client hook with `undefined` clientId on
  // a roll-up, the roll-up hook with an empty member set on a single-client
  // dashboard. Each no-ops when disabled (its own `enabled` gate), so the inactive
  // one issues no network call. The active result is picked by `isRollup`.
  const singleClientResult = useReportData(
    !isRollup && isConnectedClient ? clientId : undefined,
    widgets,
    { start: dStart, end: dEnd },
    debouncedComparison,
  );
  const rollupResult = useRollupData(
    isRollup && isConnectedClient ? rollupMemberIds : [],
    widgets,
    { start: dStart, end: dEnd },
    debouncedComparison,
  );
  const {
    widgetData: liveWidgetData,
    platformStatus,
    isError: liveRequestFailed,
    isLoading: liveIsLoading,
    isFetching: liveIsFetching,
    isUpdating: liveIsUpdating,
    hasStaleComparison,
  } = isRollup ? rollupResult : singleClientResult;

  const mockData = useMemo(() => generateMockData(widgets, comparisonEnabled), [widgets, comparisonEnabled]);

  const canvasData = useMemo(() => {
    if (!isConnectedClient || liveRequestFailed) return mockData;
    const merged: Record<string, WidgetData> = { ...liveWidgetData };
    for (const widget of widgets) {
      const mock = mockData[widget.id];
      if (!mock) continue;
      const live = liveWidgetData[widget.id];
      const unbound = !widgetHasDataBinding(widget.config as Record<string, unknown>);
      const liveErrored = !!live?.error;
      if (unbound || liveErrored) {
        merged[widget.id] = mock;
      }
    }
    return merged;
  }, [isConnectedClient, liveRequestFailed, liveWidgetData, mockData, widgets]);

  const dataNotice = useMemo<{ kind: "error" | "stale" | "info"; message: string } | null>(() => {
    if (!isConnectedClient) return null;
    const friendly = (slug: string): string =>
      ADAPTER_META[slug as keyof typeof ADAPTER_META]?.displayName ?? slug;
    if (liveRequestFailed) {
      return {
        kind: "error",
        message: "Couldn't load live data right now — showing sample numbers. We'll retry automatically.",
      };
    }
    const statuses = Object.entries(platformStatus);
    const errored = statuses.filter(([, ps]) => ps.status === "error");
    if (errored.length > 0) {
      return {
        kind: "error",
        message:
          errored.length === 1
            ? `Couldn't load data for ${friendly(errored[0]![0])}. Showing what we have.`
            : `Couldn't load data for ${errored.length} integrations. Showing what we have.`,
      };
    }
    const missing = statuses.filter(([, ps]) => ps.status === "missing");
    if (missing.length > 0) {
      return {
        kind: "info",
        message:
          missing.length === 1
            ? `Connect ${friendly(missing[0]![0])} to see live data for that widget.`
            : `Connect ${missing.length} more integrations to see live data for every widget.`,
      };
    }
    const anyStale = statuses.some(([, ps]) => ps.stale);
    if (anyStale || hasStaleComparison) {
      return {
        kind: "stale",
        message: "Showing recently cached numbers — refreshing in the background.",
      };
    }
    return null;
  }, [isConnectedClient, liveRequestFailed, platformStatus, hasStaleComparison]);

  const isUpdatingData =
    isConnectedClient && (liveIsUpdating || (liveIsFetching && !liveIsLoading));

  const selectedWidget = useMemo(
    () => widgets.find((w) => w.id === selectedWidgetId) ?? null,
    [widgets, selectedWidgetId]
  );

  useEffect(() => {
    onSelectedWidgetChange?.(selectedWidget);
  }, [selectedWidget, onSelectedWidgetChange]);

  // ── Handlers (verbatim from the report editor) ───────────────────────

  const handleWidgetSelect = useCallback((widgetId: string) => {
    setSelectedWidgetId(widgetId);
  }, []);

  const resolveCandidateMetrics = useCallback(
    (platform: string): MetricDefinition[] => {
      const connected = connectedMetricsByPlatform[platform];
      if (connected?.metrics?.length) return connected.metrics;
      const registry = getRegistryForPlatform(platform as RegistryPlatform);
      return registry?.metrics ?? [];
    },
    [connectedMetricsByPlatform],
  );

  const handleWidgetAdd = useCallback((type: WidgetType, colSpan?: ValidColSpan, rowSpan?: number, row?: number, col?: number, integrationId?: string, initialConfig?: Record<string, unknown>) => {
    const newId = crypto.randomUUID();
    let outcome: { added: boolean; toast?: { kind: "error"; message: string } } = { added: false };

    onWidgetsChange((prev) => {
      if (prev.length >= MAX_WIDGETS) {
        outcome = { added: false, toast: { kind: "error", message: `Maximum ${MAX_WIDGETS} widgets per dashboard.` } };
        return prev;
      }

      if (type === "cover-page" && prev.some((w) => w.type === "cover-page")) {
        outcome = { added: false, toast: { kind: "error", message: "Only one cover page per dashboard." } };
        return prev;
      }

      const defaults = getSmartDefaults(type as SmartDefaultWidgetType);
      const effectiveColSpan = colSpan ?? defaults.colSpan;
      const effectiveRowSpan = rowSpan ?? defaults.rowSpan;

      let baseConfig: Record<string, unknown> = { ...DEFAULT_WIDGET_CONFIGS[type], ...initialConfig };
      if (integrationId) {
        baseConfig.integrationId = integrationId;
        const candidateMetrics = resolveCandidateMetrics(integrationId);
        const defaultMetric = resolveDefaultMetric(type, candidateMetrics);
        baseConfig = applyDefaultMetricToConfig(
          baseConfig,
          type,
          defaultMetric
            ? { key: defaultMetric.key, label: defaultMetric.label, integrationId }
            : undefined,
        );
      }

      if (type === "cover-page") {
        const position = { row: 0, col: 0, colSpan: 12 as ValidColSpan, rowSpan: 3 };
        const newWidget: WidgetConfig = {
          id: newId,
          type,
          title: DEFAULT_WIDGET_TITLES[type],
          position,
          config: baseConfig,
        };
        const shifted = shiftWidgetsDown(prev, 3);
        outcome = { added: true };
        return [newWidget, ...shifted];
      }

      const hasCoverPage = prev.some((w) => w.type === "cover-page");
      const reservedRows = hasCoverPage ? 3 : 0;

      let position;
      if (row !== undefined && col !== undefined) {
        const effectiveRow = reservedRows > 0 && row < reservedRows ? reservedRows : row;
        position = { row: effectiveRow, col, colSpan: effectiveColSpan, rowSpan: effectiveRowSpan };
      } else {
        const matrix = calculateGridPlacement(prev);
        const pos = getNextAvailablePosition(matrix, effectiveColSpan, effectiveRowSpan, reservedRows);
        position = { row: pos.row, col: pos.col, colSpan: effectiveColSpan, rowSpan: effectiveRowSpan };
      }

      const autoSizeTypes = new Set(["text-block", "data-table"]);
      const newWidget: WidgetConfig = {
        id: newId,
        type,
        title: nextWidgetTitle(type, prev),
        position,
        config: baseConfig,
        ...(autoSizeTypes.has(type) ? { autoSize: true } : {}),
      };
      outcome = { added: true };
      return [...prev, newWidget];
    });

    if (outcome.added) {
      setSelectedWidgetId(newId);
    } else if (outcome.toast) {
      showCanvasToast(outcome.toast.kind, outcome.toast.message);
    }
  }, [onWidgetsChange, showCanvasToast, resolveCandidateMetrics]);

  const handleWidgetDelete = useCallback((widgetId: string) => {
    let nextSelectedId: string | null = selectedWidgetId;
    if (selectedWidgetId === widgetId) {
      const remaining = widgets.filter((w) => w.id !== widgetId);
      if (remaining.length === 0) {
        nextSelectedId = null;
      } else {
        const idx = widgets.findIndex((w) => w.id === widgetId);
        const successor = remaining[Math.min(idx, remaining.length - 1)];
        nextSelectedId = successor?.id ?? null;
      }
    }

    onWidgetsChange((prev) => prev.filter((w) => w.id !== widgetId));

    if (selectedWidgetId === widgetId) {
      setSelectedWidgetId(nextSelectedId);
    }
  }, [onWidgetsChange, selectedWidgetId, widgets]);

  const handleWidgetDuplicate = useCallback((widgetId: string) => {
    const newId = crypto.randomUUID();
    let outcome: { added: boolean; toast?: { kind: "error"; message: string } } = { added: false };

    onWidgetsChange((prev) => {
      if (prev.length >= MAX_WIDGETS) return prev;
      const original = prev.find((w) => w.id === widgetId);
      if (!original) return prev;
      if (original.type === "cover-page") {
        outcome = { added: false, toast: { kind: "error", message: "Only one cover page per dashboard." } };
        return prev;
      }
      const hasCoverPage = prev.some((w) => w.type === "cover-page");
      const reservedRows = hasCoverPage ? 3 : 0;
      const matrix = calculateGridPlacement(prev);
      const pos = getNextAvailablePosition(matrix, original.position.colSpan, original.position.rowSpan ?? 1, reservedRows);
      const duplicate: WidgetConfig = {
        ...original,
        id: newId,
        title: `${original.title} (copy)`,
        position: {
          ...original.position,
          row: pos.row,
          col: pos.col,
        },
      };
      outcome = { added: true };
      return [...prev, duplicate];
    });

    if (outcome.added) {
      setSelectedWidgetId(newId);
    } else if (outcome.toast) {
      showCanvasToast(outcome.toast.kind, outcome.toast.message);
    }
  }, [onWidgetsChange, showCanvasToast]);

  useEffect(() => {
    registerWidgetActions?.({ duplicate: handleWidgetDuplicate, delete: handleWidgetDelete });
  }, [registerWidgetActions, handleWidgetDuplicate, handleWidgetDelete]);

  const autoResizeRafRef = useRef<number | null>(null);
  const pendingResizes = useRef<Map<string, number>>(new Map());

  const handleAutoResize = useCallback((widgetId: string, newRowSpan: number) => {
    pendingResizes.current.set(widgetId, newRowSpan);

    if (autoResizeRafRef.current) cancelAnimationFrame(autoResizeRafRef.current);
    autoResizeRafRef.current = requestAnimationFrame(() => {
      const resizes = new Map(pendingResizes.current);
      pendingResizes.current.clear();
      autoResizeRafRef.current = null;

      onWidgetsSilentChange((prev) => {
        let changed = false;
        const updated = prev.map((w) => {
          const newSpan = resizes.get(w.id);
          if (newSpan !== undefined && newSpan !== (w.position.rowSpan ?? 1)) {
            changed = true;
            return { ...w, position: { ...w.position, rowSpan: newSpan } };
          }
          return w;
        });
        if (!changed) return prev;
        return updated;
      });
    });
  }, [onWidgetsSilentChange]);

  const handleResetToTemplate = useCallback(() => {
    onWidgetsChange(initialWidgets);
    setSelectedWidgetId(null);
    setShowResetDialog(false);
  }, [onWidgetsChange, initialWidgets]);

  const handleWidgetMove = useCallback(
    (widgetId: string, cell: { row: number; col: number }) => {
      onWidgetsChange((prev) =>
        prev.map((w) =>
          w.id === widgetId
            ? { ...w, position: { ...w.position, row: cell.row, col: cell.col } }
            : w,
        ),
      );
    },
    [onWidgetsChange],
  );

  const handleWidgetResize = useCallback(
    (widgetId: string, colSpan: number, rowSpan: number) => {
      onWidgetsChange((prev) =>
        prev.map((w) =>
          w.id === widgetId
            ? { ...w, position: { ...w.position, colSpan: colSpan as ValidColSpan, rowSpan } }
            : w,
        ),
      );
    },
    [onWidgetsChange],
  );

  const handlePresetAdd = useCallback((presetType: PresetType, platform: string, integrationId: string) => {
    const expandedWidgets = expandPreset(presetType, platform, integrationId);
    const presetLabel = PRESET_LABELS[presetType] ?? presetType;

    let outcome: { added: boolean; toast: { kind: "error" | "success"; message: string } } | undefined;

    onWidgetsChange((prev) => {
      if (prev.length + expandedWidgets.length > MAX_WIDGETS) {
        const remaining = MAX_WIDGETS - prev.length;
        outcome = { added: false, toast: { kind: "error", message: `Only ${remaining} slot(s) remaining — ${presetLabel} needs ${expandedWidgets.length}.` } };
        return prev;
      }

      const hasCoverPage = prev.some((w) => w.type === "cover-page");
      const reservedRows = hasCoverPage ? 3 : 0;
      const workingWidgets = [...prev];
      const positioned: WidgetConfig[] = [];

      for (const widget of expandedWidgets) {
        const matrix = calculateGridPlacement(workingWidgets);
        const pos = getNextAvailablePosition(matrix, widget.colSpan as ValidColSpan, widget.rowSpan, reservedRows);
        const positionedWidget: WidgetConfig = {
          id: widget.id,
          type: widget.type,
          title: widget.title,
          position: { row: pos.row, col: pos.col, colSpan: widget.colSpan as ValidColSpan, rowSpan: widget.rowSpan },
          config: widget.config,
          ...(widget.autoSize ? { autoSize: true } : {}),
        };
        positioned.push(positionedWidget);
        workingWidgets.push(positionedWidget);
      }

      outcome = {
        added: true,
        toast: { kind: "success", message: `${presetLabel} added — ${expandedWidgets.length} widget${expandedWidgets.length > 1 ? "s" : ""}` },
      };
      return [...prev, ...positioned];
    });

    const result = outcome as { added: boolean; toast: { kind: "error" | "success"; message: string } } | undefined;
    if (result?.added) {
      setSelectedWidgetId(expandedWidgets[0]!.id);
    }
    if (result) {
      showCanvasToast(result.toast.kind, result.toast.message);
    }
  }, [onWidgetsChange, showCanvasToast]);

  const handleEmptyDrop = useCallback((e: DragEvent) => {
    const raw = e.dataTransfer?.getData("text/plain");
    if (!raw) return;
    const result = parseDragPayload(raw, isKnownDropType);
    if (!result.ok) {
      showCanvasToast("error", `Couldn't add that widget — ${result.reason}.`);
      return;
    }
    const data = result.payload;
    queueMicrotask(() => {
      if (isPresetType(data.type)) {
        handlePresetAdd(data.type as PresetType, data.platform ?? "", data.integrationId ?? "");
        return;
      }
      handleWidgetAdd(
        data.type as WidgetType,
        data.colSpan as ValidColSpan,
        data.rowSpan,
        undefined,
        undefined,
        data.integrationId
      );
    });
  }, [handleWidgetAdd, handlePresetAdd, showCanvasToast]);

  const handlePaletteDndDrop = useCallback(
    (data: PaletteDragData, cell: GridCell | null) => {
      if (isPresetType(data.type)) {
        handlePresetAdd(data.type as PresetType, data.platform ?? "", data.integrationId ?? "");
        return;
      }
      handleWidgetAdd(
        data.type as WidgetType,
        data.colSpan as ValidColSpan,
        data.rowSpan,
        cell?.row,
        cell?.col,
        data.integrationId,
        data.config,
      );
    },
    [handleWidgetAdd, handlePresetAdd],
  );
  const { registerDropHandler } = useReportDnd();
  useEffect(() => {
    registerDropHandler(handlePaletteDndDrop);
  }, [registerDropHandler, handlePaletteDndDrop]);

  // ── Keyboard Shortcuts (verbatim from the report editor) ─────────────

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

      const isMod = e.metaKey || e.ctrlKey;

      if (isMod && e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        onUndo();
        return;
      }

      if (isMod && ((e.key === "z" && e.shiftKey) || e.key === "y")) {
        e.preventDefault();
        onRedo();
        return;
      }

      if (isMod && e.key === "d") {
        e.preventDefault();
        if (selectedWidgetId) handleWidgetDuplicate(selectedWidgetId);
        return;
      }

      if ((e.key === "Delete" || e.key === "Backspace") && selectedWidgetId) {
        e.preventDefault();
        handleWidgetDelete(selectedWidgetId);
        return;
      }

      if (e.key === "Escape" && selectedWidgetId) {
        e.preventDefault();
        setSelectedWidgetId(null);
        return;
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onUndo, onRedo, selectedWidgetId, handleWidgetDuplicate, handleWidgetDelete]);

  return (
    <>
      {/* Canvas-level data affordances (identical chrome to the report editor). */}
      <AnimatePresence>
        {dataNotice?.kind === "error" && dataNotice.message !== dismissedErrorMessage ? (
          <motion.div
            key="data-error"
            className="absolute left-1/2 top-3 z-20 -translate-x-1/2 flex items-center gap-2 px-3 py-1.5 rounded-2xl bg-background shadow-xs"
            style={{ border: "0.5px solid var(--hairline)" }}
            data-testid="canvas-data-error"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
          >
            <span className="text-[13px] text-destructive">{dataNotice.message}</span>
            <button
              type="button"
              onClick={() => setDismissedErrorMessage(dataNotice.message)}
              aria-label="Dismiss"
              data-testid="canvas-data-error-dismiss"
              className="-mr-1 ml-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-destructive/70 hover:bg-destructive/10 hover:text-destructive cursor-pointer transition-colors"
            >
              <X size={12} weight="bold" />
            </button>
          </motion.div>
        ) : isUpdatingData ? (
          <motion.div
            key="updating-affordance"
            className="pointer-events-none absolute left-1/2 top-3 z-20 -translate-x-1/2 flex items-center gap-2 px-3 py-1.5 rounded-2xl bg-background shadow-xs"
            style={{ border: "0.5px solid var(--hairline)" }}
            data-testid="canvas-updating-indicator"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
          >
            <span className="text-[13px] text-muted-foreground">Updating…</span>
          </motion.div>
        ) : dataNotice?.kind === "stale" ? (
          <motion.div
            key="data-stale"
            className="absolute left-1/2 top-3 z-20 -translate-x-1/2 flex items-center gap-2 px-3 py-1.5 rounded-2xl bg-background shadow-xs"
            style={{ border: "0.5px solid var(--hairline)" }}
            data-testid="canvas-data-stale"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
          >
            <span className="text-[13px] text-muted-foreground">{dataNotice.message}</span>
          </motion.div>
        ) : dataNotice?.kind === "info" ? (
          <motion.div
            key="data-info"
            className="absolute left-1/2 top-3 z-20 -translate-x-1/2 flex items-center gap-2 px-3 py-1.5 rounded-2xl bg-background shadow-xs"
            style={{ border: "0.5px solid var(--hairline)" }}
            data-testid="canvas-data-info"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
          >
            <span className="text-[13px] text-muted-foreground">{dataNotice.message}</span>
          </motion.div>
        ) : null}
      </AnimatePresence>
      <ZoomWrapper zoomLevel={zoomLevel} leftShift={paperLeftShift} panX={panX} panY={panY} onMeasure={handleMeasure}>
        <DashboardCanvas
          widgets={widgets}
          widgetData={canvasData}
          selectedWidgetId={selectedWidgetId}
          onWidgetSelect={handleWidgetSelect}
          onWidgetDelete={handleWidgetDelete}
          onWidgetDuplicate={handleWidgetDuplicate}
          onAutoResize={handleAutoResize}
          editable
          reportDateRangeStart={reportDateRangeStart}
          reportDateRangeEnd={reportDateRangeEnd}
          selectedClientName={selectedClientName}
          selectedClientLogoUrl={selectedClientLogoUrl}
          format={format}
          onWidgetMove={handleWidgetMove}
          onWidgetResize={handleWidgetResize}
          zoomLevel={zoomLevel}
          measuredWidthPx={measuredWidthPx}
          onEmptyQuickAdd={(type) => handleWidgetAdd(type)}
          onEmptyAddAt={(type, pos) =>
            handleWidgetAdd(
              type as WidgetType,
              pos.colSpan as ValidColSpan,
              pos.rowSpan,
              pos.row,
              pos.col,
            )
          }
          onEmptyDrop={handleEmptyDrop}
        />
      </ZoomWrapper>

      {/* Reset confirmation dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Layout?</DialogTitle>
            <DialogDescription>
              This will restore the original layout. Your changes can be undone with Ctrl+Z.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleResetToTemplate} data-testid="confirm-reset-btn">
              Reset Layout
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Toast toast={canvasToast} />

      {/* Cmd+K command palette for adding widgets */}
      <WidgetCommandPalette
        onAddWidget={(type, colSpan, rowSpan) => handleWidgetAdd(type, colSpan, rowSpan)}
      />
    </>
  );
}
